import { Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers';
import { useAppDispatch } from '../../store/store';
import { getSchedulerSettings, saveSchedulerSettings } from '../../store/officeScheduler.slice';
import { SchedulerSettings } from '../../Models/Scheduling/SchedulerSettings.model';

interface AdminSchedulingPanelProps {
    title: string;
}

const AdminSchedulingPanel = ({ title }: AdminSchedulingPanelProps) => {
    const dispatch = useAppDispatch();
    const { schedulerSettings } = useSelector((state: RootState) => state.officeSchedulerSlice);
    const { isLoading } = useSelector((state: RootState) => state.shared);
    
    const [interimHours, setInterimHours] = useState<number>(4);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        document.title = title;
    }, [title]);

    useEffect(() => {
        dispatch(getSchedulerSettings());
    }, [dispatch]);

    useEffect(() => {
        if (schedulerSettings) {
            setInterimHours(schedulerSettings.InterimCallAttemptHours);
        }
    }, [schedulerSettings]);

    const validateInput = (value: number): boolean => {
        if (value < 1 || value > 24) {
            setError('Hours must be between 1 and 24');
            return false;
        }
        setError('');
        return true;
    };

    const handleInterimHoursChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(event.target.value);
        setInterimHours(value);
        validateInput(value);
    };

    const handleSave = useCallback(async () => {
        if (!validateInput(interimHours)) {
            return;
        }

        const newSettings = new SchedulerSettings();
        if (schedulerSettings) {
            newSettings.Id = schedulerSettings.Id;
        }
        newSettings.InterimCallAttemptHours = interimHours;
        
        dispatch(saveSchedulerSettings(newSettings));
    }, [interimHours, schedulerSettings, dispatch]);

    return (
        <Box p={3}>
            <Typography variant="h5" gutterBottom>
                Scheduler Call Queue Settings
            </Typography>
            <Paper elevation={3} style={{ padding: '24px', marginTop: '16px' }}>
                <Stack spacing={3}>
                    <Typography variant="h6">
                        Interim Time Between Call Attempts
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Set the minimum number of hours that must pass before a member who was not reached 
                        can be assigned to the call queue again. This prevents schedulers from calling the 
                        same members too frequently.
                    </Typography>
                    
                    <TextField
                        label="Hours Between Call Attempts"
                        type="number"
                        value={interimHours}
                        onChange={handleInterimHoursChange}
                        error={!!error}
                        helperText={error || 'Valid range: 1-24 hours'}
                        InputProps={{
                            inputProps: { 
                                min: 1, 
                                max: 24 
                            }
                        }}
                        sx={{ maxWidth: '300px' }}
                    />

                    <Box>
                        <Button 
                            variant="contained" 
                            color="primary"
                            onClick={handleSave}
                            disabled={isLoading || !!error}
                        >
                            Save Settings
                        </Button>
                    </Box>
                </Stack>
            </Paper>
        </Box>
    );
};

export default AdminSchedulingPanel;
